import { Link, useRouterState } from '@tanstack/react-router';
import {
  HouseIcon,
  ConfettiIcon,
  NewspaperIcon,
  UserCircleIcon,
  CowboyHatIcon,
} from '@phosphor-icons/react';
import { useActionBarContext } from '@/lib/context/ActionBarContext';
import { ActionBar } from '@/components/ActionBar';
import { useUserProfile } from '@/lib/hooks/useUserProfile';
import { usePendingPostsCount } from '@/lib/hooks/usePosts';

export function Navigation() {
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const { config } = useActionBarContext();
  const { isAdmin } = useUserProfile();
  const userIsAdmin = isAdmin();
  const { data: pendingPostsCount = 0 } = usePendingPostsCount(userIsAdmin);

  const showActionBar = !!config?.actions?.length && config?.visible !== false;

  const navItems = [
    { path: '/', icon: HouseIcon, label: 'Home' },
    { path: '/events', icon: ConfettiIcon, label: 'Events' },
    { path: '/feed', icon: NewspaperIcon, label: 'Feed' },
    ...(userIsAdmin
      ? [{ path: '/admin', icon: UserCircleIcon, label: 'Admin' }]
      : []),
    { path: '/account', icon: CowboyHatIcon, label: 'Account' },
  ];

  return (
    <nav
      className={`flex-shrink-0 py-6 bg-background border-t border-slate-600 pb-[max(1.5rem,env(safe-area-inset-bottom))] z-40 ${showActionBar ? 'pt-0' : ''}`}
    >
      {showActionBar && <ActionBar config={config!} />}
      <div className="flex justify-around items-center py-2 px-4 w-full max-w-4xl mx-auto">
        {navItems.map(({ path, icon: Icon, label }) => {
          const isActive =
            path === '/'
              ? currentPath === '/'
              : currentPath === path || currentPath.startsWith(path + '/');
          const showPendingBadge =
            path === '/feed' && userIsAdmin && pendingPostsCount > 0;

          return (
            <Link
              key={path}
              to={path}
              className="flex flex-col items-center justify-center gap-1 py-2 px-4 text-foreground no-underline transition-colors"
            >
              <div className="relative">
                <Icon
                  weight={isActive ? 'fill' : 'regular'}
                  size={24}
                  className={isActive ? 'text-primary' : 'text-foreground'}
                />
                {showPendingBadge && (
                  <>
                    <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-red-400 ring-2 ring-background" />
                    <span className="sr-only">Pending posts require review</span>
                  </>
                )}
              </div>
              <span className="text-xs">{label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
